import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Camera, MapPin, Trophy, Flame, Star, Globe, Layers, Calendar, TrendingUp } from 'lucide-react';
import api, { RARITY_META } from '../lib/api';

export default function Stats() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get('/stats/me').then(r => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="p-6 text-xs text-muted-foreground">Loading statistics…</div>;

  const maxDaily = Math.max(1, ...d.daily_series.map(x => x.count));
  const maxDow = Math.max(1, ...d.day_of_week.map(x => x.count));
  const rarityOrder = ['common','uncommon','rare','epic','legendary','mythic'];
  const maxCatCount = Math.max(1, ...d.species_by_category.map(c => c.count));

  return (
    <div className="space-y-6" data-testid="stats-root">
      <div>
        <p className="text-[10px] uppercase tracking-[0.25em] text-accent">Explorer Statistics</p>
        <h2 className="font-serif text-3xl">Your lifetime</h2>
      </div>

      {/* Top metrics */}
      <div className="grid grid-cols-3 gap-2">
        <Metric icon={Sparkles} label="Species" value={d.species_discovered} />
        <Metric icon={Trophy} label="Score Total" value={d.discovery_score_total?.toLocaleString?.()} accent="#E9C46A" />
        <Metric icon={Star} label="Best Score" value={d.highest_discovery_score?.toLocaleString?.()} />
        <Metric icon={Camera} label="Sightings" value={d.observations_total} />
        <Metric icon={TrendingUp} label="Avg Score" value={d.average_discovery_score} />
        <Metric icon={Star} label="Photo Quality" value={`${Math.round((d.average_photo_quality || 0) * 100)}%`} />
        <Metric icon={Flame} label="Streak" value={`${d.daily_streak}d`} />
        <Metric icon={Flame} label="Longest" value={`${d.longest_streak}d`} accent="#F4A261" />
        <Metric icon={Layers} label="Cards" value={d.battle_cards} />
        <Metric icon={Globe} label="World Firsts" value={d.world_firsts} accent="#FF6EC7" />
        <Metric icon={MapPin} label="Region Firsts" value={d.regional_firsts} accent="#8ECAE6" />
        <Metric icon={Star} label="Season Firsts" value={d.seasonal_firsts} accent="#95D5B2" />
      </div>

      {/* This period */}
      <section>
        <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">This Period</p>
        <div className="grid grid-cols-3 gap-2">
          <SmallMetric label="Today" value={d.observations_today} />
          <SmallMetric label="Month sp." value={d.species_this_month} />
          <SmallMetric label="Year sp." value={d.species_this_year} />
        </div>
      </section>

      {/* Daily chart */}
      <section>
        <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">Daily activity (last {d.daily_series.length}d)</p>
        <div className="rounded-3xl bg-card border border-white/5 p-4">
          {d.daily_series.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">No activity yet.</p>
          ) : (
            <div className="flex items-end gap-[2px] h-24" data-testid="daily-chart">
              {d.daily_series.map(day => (
                <motion.div
                  key={day.date}
                  initial={{ height: 0 }}
                  animate={{ height: `${(day.count / maxDaily) * 100}%` }}
                  transition={{ duration: 0.5 }}
                  className="flex-1 min-w-[3px] rounded-t bg-gradient-to-t from-accent/50 to-accent"
                  title={`${day.date}: ${day.count} sightings`}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Category radar (bar chart) */}
      <section>
        <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">Species by Category</p>
        <div className="rounded-3xl bg-card border border-white/5 p-4 space-y-2" data-testid="category-chart">
          {d.species_by_category.map(c => (
            <div key={c.category} className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span>{c.category}</span>
                <span className="font-mono text-muted-foreground">{c.count}</span>
              </div>
              <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${(c.count / maxCatCount) * 100}%` }}
                  transition={{ duration: 0.6 }}
                  className="h-full bg-accent/70"
                />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Rarity distribution */}
      <section>
        <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">By Rarity</p>
        <div className="grid grid-cols-6 gap-1.5" data-testid="rarity-chart">
          {rarityOrder.map(r => (
            <div key={r} className="rounded-xl bg-card border border-white/5 py-2 text-center">
              <p className="font-mono text-sm tabular-nums" style={{ color: RARITY_META[r]?.color }}>{d.rarity_counts[r] || 0}</p>
              <p className="text-[8px] uppercase tracking-widest text-muted-foreground">{r.slice(0, 5)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Day-of-week + Best month */}
      <section className="grid grid-cols-3 gap-3">
        <div className="col-span-2 rounded-3xl bg-card border border-white/5 p-4" data-testid="dow-chart">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Most active days</p>
          <div className="flex items-end gap-2 h-20">
            {d.day_of_week.map(dw => (
              <div key={dw.day} className="flex-1 flex flex-col items-center gap-1">
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: `${(dw.count / maxDow) * 100}%` }}
                  transition={{ duration: 0.5 }}
                  className="w-full rounded-t bg-primary/70"
                />
                <span className="text-[9px] text-muted-foreground">{dw.day}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-3xl bg-card border border-white/5 p-4 flex flex-col justify-center items-center text-center">
          <Calendar className="w-4 h-4 text-accent mb-1" />
          <p className="text-[9px] uppercase tracking-widest text-muted-foreground">Best Month</p>
          <p className="font-serif text-base mt-1">{d.best_month || '—'}</p>
          <p className="mt-2 text-[9px] uppercase tracking-widest text-muted-foreground">Peak day</p>
          <p className="font-serif text-base">{d.most_active_dow || '—'}</p>
        </div>
      </section>

      {/* Top habitats */}
      {d.top_habitats.length > 0 && (
        <section>
          <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">Favourite habitats</p>
          <div className="rounded-3xl bg-card border border-white/5 overflow-hidden">
            {d.top_habitats.map(h => (
              <div key={h.area} className="flex items-center justify-between p-3 border-b border-white/5 last:border-0 text-xs">
                <span className="truncate">{h.area}</span>
                <span className="font-mono text-accent">{h.count}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function Metric({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-2xl bg-card border border-white/5 p-3">
      <Icon className="w-3.5 h-3.5 mb-1" strokeWidth={2} style={{ color: accent || '#E9C46A' }} />
      <p className="font-mono font-semibold text-lg tabular-nums" style={{ color: accent }}>{value ?? '—'}</p>
      <p className="text-[9px] uppercase tracking-widest text-muted-foreground">{label}</p>
    </div>
  );
}

function SmallMetric({ label, value }) {
  return (
    <div className="rounded-xl bg-card border border-white/5 p-2 text-center">
      <p className="font-mono text-base tabular-nums">{value ?? 0}</p>
      <p className="text-[9px] uppercase tracking-widest text-muted-foreground">{label}</p>
    </div>
  );
}
